import { z } from 'zod';
import { ROLES } from '../constants/rbac';
import { isoDateSchema, sortDirectionSchema, uuidSchema } from './common';

/**
 * Листы — остальные вкладки рабочей книги (решение Р-22).
 *
 * Отличие от колонок кандидата: те описаны в коде (`constants/columns.ts`)
 * и меняются релизом, а эти описываются строками в базе и заводятся из
 * интерфейса. Схемы здесь — общий язык API и фронтенда: сервер по ним
 * проверяет входящее, фронтенд по ним же строит таблицу и редакторы.
 *
 * Почему колонки кандидата сюда не переехали — в Р-22: за это пришлось бы
 * переставить на новые рельсы маску телефона, каскад города, скрытие
 * паспортов, историю по полям, импорт, выгрузку и отчёты.
 */

// ── Типы колонок ──────────────────────────────────────────

export const SHEET_COLUMN_TYPES = [
  'text',
  'longtext',
  'number',
  /** Число с денежным выводом: «3 993 ₽». Отдельно от number ради вида */
  'money',
  'date',
  'phone',
  'boolean',
  /** Выбор из списка вариантов, заданного на самой колонке */
  'select',
  'multiselect',
  /** Ссылка на строку другого листа */
  'ref',
  /** Сотрудник системы */
  'user',
  /** Адрес страницы: в «Рекламе РБ» половина колонок — ссылки на группы */
  'link',
  /** Считается правилом, руками не правится */
  'computed',
] as const;
export type SheetColumnType = (typeof SHEET_COLUMN_TYPES)[number];

export const SHEET_COLUMN_TYPE_LABELS: Record<SheetColumnType, string> = {
  text: 'Текст',
  longtext: 'Текст в несколько строк',
  number: 'Число',
  money: 'Сумма',
  date: 'Дата',
  phone: 'Телефон',
  boolean: 'Да / нет',
  select: 'Выбор из списка',
  multiselect: 'Несколько из списка',
  ref: 'Ссылка на другой лист',
  user: 'Сотрудник',
  link: 'Ссылка на страницу',
  computed: 'Вычисляется',
};

/** Типы, в которых значение хранится числом. */
export const NUMERIC_SHEET_TYPES: readonly SheetColumnType[] = ['number', 'money'];

const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Цвет задаётся шестью шестнадцатеричными знаками: #RRGGBB');

const roleSchema = z.enum(ROLES);

// ── Варианты выбора ───────────────────────────────────────

/**
 * Вариант для колонок `select` и `multiselect`.
 *
 * Цвет варианта — это и есть условная заливка для самого частого случая:
 * «Оплачено» зелёным, «Закончилась реклама» серым. Отдельные правила
 * (ниже) нужны только там, где красит не значение, а сравнение —
 * просроченная дата, сумма выше порога.
 */
export const sheetSelectOptionSchema = z.object({
  value: z.string().min(1).max(160),
  label: z.string().max(160).optional(),
  color: hexColorSchema.optional(),
  textColor: hexColorSchema.optional(),
});
export type SheetSelectOption = z.infer<typeof sheetSelectOptionSchema>;

// ── Правила вычисления ────────────────────────────────────

/**
 * Не язык формул, а перечисляемый набор.
 *
 * Причина в спецификации: «=A1+B2» пришлось бы разбирать, проверять
 * на цикл и объяснять заказчику, почему формула не считается. Шесть
 * правил закрывают всё, что в книге считалось руками.
 */
export const sheetComputedRuleSchema = z.discriminatedUnion('kind', [
  /** День недели по дате: колонка «День недели» в «Графике» и «Занятости» */
  z.object({ kind: z.literal('weekday'), from: z.string().max(64) }),
  /**
   * Дата плюс число дней. Число берётся из колонки (`daysFrom`) или задано
   * (`days`). «Пер-ка»: дата окончания = дата выезда + число смен.
   */
  z.object({
    kind: z.literal('dateOffset'),
    from: z.string().max(64),
    days: z.number().int().min(-3650).max(3650).optional(),
    daysFrom: z.string().max(64).optional(),
  }),
  /** Сколько дней осталось до даты. Отрицательное — дата прошла */
  z.object({ kind: z.literal('daysUntil'), from: z.string().max(64) }),
  /** Первая буква значения: буквенный указатель в «ЧС» */
  z.object({ kind: z.literal('firstLetter'), from: z.string().max(64) }),
  /** Склейка значений: «Срок вахты» = объект + число смен */
  z.object({
    kind: z.literal('concat'),
    parts: z.array(z.string().max(64)).min(1).max(6),
    separator: z.string().max(8).default(' '),
  }),
  /** Номер строки по порядку — как в первой колонке каталогов */
  z.object({ kind: z.literal('rowNumber') }),
  /**
   * Значение из строки, на которую ссылается колонка-ссылка.
   * Ставка из «Оплаты» по проекту, условия объекта из каталога.
   */
  z.object({
    kind: z.literal('lookup'),
    /** Ключ колонки типа `ref` в этой же строке */
    from: z.string().max(64),
    /** Какую колонку взять из найденной строки */
    take: z.string().max(64),
  }),
]);
export type SheetComputedRule = z.infer<typeof sheetComputedRuleSchema>;

export const SHEET_RULE_LABELS: Record<SheetComputedRule['kind'], string> = {
  weekday: 'День недели по дате',
  dateOffset: 'Дата плюс дни',
  daysUntil: 'Дней до даты',
  firstLetter: 'Первая буква',
  concat: 'Склейка значений',
  rowNumber: 'Номер по порядку',
  lookup: 'Значение с другого листа',
};

// ── Правила заливки ───────────────────────────────────────

export const sheetFillConditionSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('equals'), value: z.string().max(160) }),
  z.object({ op: z.literal('contains'), value: z.string().max(160) }),
  z.object({ op: z.literal('empty') }),
  z.object({ op: z.literal('notEmpty') }),
  /** Дата в ячейке уже прошла: просроченная оплата рекламы */
  z.object({ op: z.literal('datePassed') }),
  /** Дата наступит в течение N дней: «писать за 2-3 дня до конца вахты» */
  z.object({
    op: z.literal('dateWithinDays'),
    days: z.number().int().min(0).max(365),
  }),
  z.object({ op: z.literal('numberAbove'), value: z.number() }),
  z.object({ op: z.literal('numberBelow'), value: z.number() }),
]);
export type SheetFillCondition = z.infer<typeof sheetFillConditionSchema>;

export const sheetFillRuleSchema = z.object({
  when: sheetFillConditionSchema,
  bg: hexColorSchema,
  fg: hexColorSchema.optional(),
  /** Красить ячейку или всю строку */
  scope: z.enum(['cell', 'row']).default('cell'),
});
export type SheetFillRule = z.infer<typeof sheetFillRuleSchema>;

// ── Вид ячейки, поставленный рукой ────────────────────────

/**
 * Оформление одной ячейки.
 *
 * Поля короткие намеренно: мешок лежит в `jsonb` у каждой строки листа,
 * и на «Занятости» это полторы тысячи строк по тридцать ключей. `bold`
 * вместо `b` — это лишние сорок килобайт в каждом ответе ради читаемости
 * того, на что человек не смотрит.
 *
 * Заливки здесь нет: она живёт в своём `fills` с самого начала, её знает
 * импорт из книги и правила покраски. Разносить её сюда — переписывать
 * работающее ради симметрии.
 */
export const sheetCellStyleSchema = z.object({
  /** Жирный */
  b: z.boolean().optional(),
  /** Курсив */
  i: z.boolean().optional(),
  /** Подчёркнутый */
  u: z.boolean().optional(),
  /** Цвет текста */
  fg: hexColorSchema.optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  /** Переносить текст по словам, независимо от плотности листа */
  wrap: z.boolean().optional(),
  /**
   * Границы — подмножество букв `tblr` (top, bottom, left, right).
   * Строкой, а не четырьмя признаками: так короче в мешке и так же
   * коротко проверяется.
   */
  border: z
    .string()
    .max(4)
    .regex(/^[tblr]*$/)
    .optional(),
});
export type SheetCellStyle = z.infer<typeof sheetCellStyleSchema>;

/** Оформление строки: ключ колонки → вид ячейки. */
export type SheetCellStyles = Record<string, SheetCellStyle>;

/**
 * Как показывать значение колонки.
 *
 * Отдельно от типа: тип менять нельзя, потому что в строках уже лежат
 * значения (`sheetColumnUpdateSchema` его прямо исключает), а формат —
 * это про вид, и его меняют часто. Пока формата не было, «показать
 * сумму без копеек» означало сменить тип и потерять данные.
 */
export const SHEET_FORMATS = [
  'plain',
  'number',
  'grouped',
  'money',
  'percent',
  'date',
  'dateWeekday',
  'phone',
  'text',
] as const;
export type SheetFormat = (typeof SHEET_FORMATS)[number];

// ── Колонка ───────────────────────────────────────────────

export const sheetColumnSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  label: z.string(),
  hint: z.string().nullable(),
  type: z.enum(SHEET_COLUMN_TYPES),
  /**
   * Для системного листа — имя типизированной колонки таблицы-источника.
   * Пусто означает, что значение лежит в мешке дополнительных признаков.
   */
  sourceField: z.string().nullable(),
  options: z.array(sheetSelectOptionSchema),
  /** Код листа, на строки которого ссылается колонка типа `ref` */
  refSheetCode: z.string().nullable(),
  /**
   * Каскад: ключ колонки в этой же строке, значением которой сужается
   * список вариантов. «Объект работы» — только объекты своего проекта.
   */
  refFilterKey: z.string().nullable(),
  rule: sheetComputedRuleSchema.nullable(),
  fillRules: z.array(sheetFillRuleSchema),
  width: z.number().int(),
  minWidth: z.number().int().nullable(),
  maxWidth: z.number().int().nullable(),
  align: z.enum(['left', 'center', 'right']),
  pinned: z.enum(['left']).nullable(),
  /** Группа колонок в шапке: «Беларусь» и «Россия» в «Занятости» */
  groupLabel: z.string().nullable(),
  editable: z.boolean(),
  sortable: z.boolean(),
  filterable: z.boolean(),
  visibleByDefault: z.boolean(),
  required: z.boolean(),
  /**
   * Объединять одинаковые значения соседних строк по вертикали.
   * Вид «Рекл.РБ», где одно размещение занимает четыре строки ссылок.
   */
  mergeRepeats: z.boolean(),
  /** Персональные данные: значение не пишется в журнал */
  isPii: z.boolean(),
  hiddenForRoles: z.array(roleSchema),
  readonlyForRoles: z.array(roleSchema),
  sortOrder: z.number().int(),
  /** Как показывать значение: сумма, процент, дата с днём недели */
  format: z.enum(SHEET_FORMATS),
  /** Переносить текст в колонке по словам */
  wrap: z.boolean(),
});
export type SheetColumn = z.infer<typeof sheetColumnSchema>;

// ── Лист ──────────────────────────────────────────────────

export const SHEET_KINDS = ['system', 'custom'] as const;
export type SheetKind = (typeof SHEET_KINDS)[number];

/** Таблицы, над которыми стоят системные листы. */
export const SHEET_SOURCE_TABLES = ['candidate', 'work_object', 'blacklist'] as const;
export type SheetSourceTable = (typeof SHEET_SOURCE_TABLES)[number];

export const sheetSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(SHEET_KINDS),
  sourceTable: z.enum(SHEET_SOURCE_TABLES).nullable(),
  /** Цвет ярлыка внизу; пусто — как у всех */
  color: z.string().nullable(),
  /** Какая колонка представляет строку, когда на неё ссылаются */
  rowLabelKey: z.string().nullable(),
  visibleToRoles: z.array(roleSchema),
  editableByRoles: z.array(roleSchema),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  isShared: z.boolean(),
  sortOrder: z.number().int(),
  /** Сколько строк на листе — для меню и для подсказки о похожих листах */
  rowCount: z.number().int().optional(),
  /**
   * Можно ли заводить и удалять строки прямо на листе.
   *
   * У своих листов — всегда, у системных — только там, где своего раздела
   * нет: каталог объектов правится с листа, а запись чёрного списка
   * заводится в своём разделе, где нормализуются ФИО и телефоны.
   * Признак приходит с сервера, чтобы кнопки на экране не решали этот
   * вопрос каждая по-своему.
   */
  canAddRows: z.boolean(),
});
export type Sheet = z.infer<typeof sheetSchema>;

export const sheetSectionSchema = z.object({
  id: uuidSchema,
  title: z.string().nullable(),
  /** Врезка на всю ширину: условия вахты, требования к документам */
  body: z.string().nullable(),
  color: hexColorSchema.nullable(),
  sortOrder: z.number(),
  /**
   * Сколько строк в секции на всём листе, без учёта фильтра вида.
   *
   * По нему видно, что секция — не разделитель, а записка: врезка
   * с шаблоном сообщения или требованиями к документам строк не имеет.
   * Считать это по присланным строкам нельзя: фильтр вида их скрывает,
   * и чужой каталог выглядел бы запиской.
   */
  rowCount: z.number().int(),
});
export type SheetSection = z.infer<typeof sheetSectionSchema>;

// ── Строка ────────────────────────────────────────────────

/** Значения ячеек: ключ колонки → значение. */
export const sheetRowDataSchema = z.record(z.string(), z.unknown());
export type SheetRowData = z.infer<typeof sheetRowDataSchema>;

export const sheetRowSchema = z.object({
  id: z.string(),
  sectionId: uuidSchema.nullable(),
  data: sheetRowDataSchema,
  /** Посчитанное сервером: вычисляемые колонки */
  computed: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  /** Подписи для колонок-ссылок: в data лежит идентификатор */
  labels: z.record(z.string(), z.string()),
  /** Ручная заливка: ключ колонки → цвет фона */
  fills: z.record(z.string(), z.string()),
  /** Оформление, поставленное рукой: ключ колонки → вид ячейки */
  styles: z.record(z.string(), sheetCellStyleSchema),
  sortOrder: z.number(),
  /** Место объекта во всём каталоге, до поиска и пагинации. */
  position: z.number().int().positive().optional(),
  /** Группа каталога и номер в ней, рассчитанные до пагинации. */
  projectGroup: z.object({
    key: z.string(),
    label: z.string(),
    position: z.number().int().positive(),
    total: z.number().int().positive(),
  }).optional(),
  deletedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type SheetRow = z.infer<typeof sheetRowSchema>;

export const workObjectCatalogLayoutSchema = z.object({
  collapsedGroups: z.array(z.string().max(4000)),
});
export type WorkObjectCatalogLayout = z.infer<typeof workObjectCatalogLayoutSchema>;
export const workObjectCatalogGroupPatchSchema = z.object({
  key: z.string().min(1).max(4000),
  collapsed: z.boolean(),
});

// ── Объединение ячеек ─────────────────────────────────────

/**
 * Объединённая область.
 *
 * Якорь — строка и ключ колонки, а не номера: номера переставляются
 * сортировкой, и запись «строки 5–8» после неё накрыла бы случайных соседей.
 */
export const sheetMergeSchema = z.object({
  id: uuidSchema,
  anchorRowId: z.string(),
  anchorKey: z.string().max(64),
  rowSpan: z.number().int().min(1).max(200),
  colSpan: z.number().int().min(1).max(40),
});
export type SheetMerge = z.infer<typeof sheetMergeSchema>;

export const sheetMergeWriteSchema = z.object({
  anchorRowId: z.string(),
  anchorKey: z.string().max(64),
  rowSpan: z.number().int().min(1).max(200),
  colSpan: z.number().int().min(1).max(40),
});

// ── Вид листа ─────────────────────────────────────────────

export const sheetSortSchema = z.object({
  field: z.string().max(64),
  dir: sortDirectionSchema.default('asc'),
});

export const sheetFilterSchema = z.object({
  field: z.string().max(64),
  op: z.enum([
    'eq',
    'neq',
    'contains',
    'notContains',
    'in',
    'isEmpty',
    'isNotEmpty',
    'gt',
    'gte',
    'lt',
    'lte',
  ]),
  value: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(z.union([z.string(), z.number()])),
    ])
    .optional(),
});
export type SheetFilter = z.infer<typeof sheetFilterSchema>;

export const sheetViewConfigSchema = z.object({
  /** Однократное обновление телефонных колонок Базы; последующий выбор человека сохраняется. */
  basePhoneLayoutVersion: z.literal(2).optional(),
  /** Старые виды Базы скрыли дублирующий №; последующий выбор сохраняется. */
  baseRowNumberLayoutVersion: z.literal(1).optional(),
  columns: z.array(z.string()).max(80).default([]),
  widths: z.record(z.string(), z.number().int().min(40).max(900)).default({}),
  pinned: z.array(z.string()).max(8).default([]),
  sort: z.array(sheetSortSchema).max(3).default([]),
  filters: z.array(sheetFilterSchema).max(20).default([]),
  search: z.string().max(100).optional(),
  density: z.enum(['compact', 'normal', 'comfortable', 'lines2', 'lines4']).default('normal'),
});
export type SheetViewConfig = z.infer<typeof sheetViewConfigSchema>;

export const sheetViewSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  config: sheetViewConfigSchema,
  isShared: z.boolean(),
  isDefault: z.boolean(),
  ownerId: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type SheetView = z.infer<typeof sheetViewSchema>;

/** Всё, что нужно, чтобы нарисовать лист. */
export const sheetDetailSchema = z.object({
  sheet: sheetSchema,
  columns: z.array(sheetColumnSchema),
  sections: z.array(sheetSectionSchema),
  /** Объединённые области: рисуются только в собственном порядке листа */
  merges: z.array(sheetMergeSchema),
  views: z.array(sheetViewSchema),
  /** Может ли текущий пользователь править данные листа */
  canEdit: z.boolean(),
  /** Может ли менять структуру и права */
  canManage: z.boolean(),
});
export type SheetDetail = z.infer<typeof sheetDetailSchema>;

// ── Запросы ───────────────────────────────────────────────

export const sheetRowsQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  /** Фильтры и сортировка приходят строкой JSON: их собирает вид */
  filters: z.string().max(4000).optional(),
  sort: z.string().max(500).optional(),
  includeDeleted: z.coerce.boolean().default(false),
  /*
    Потолок в двадцать тысяч, а не в две.

    Две ставились под самый большой лист рабочей книги — 1440 строк.
    На «Базе» их 11 983, и прежний потолок означал бы лист, у которого
    восемь тысяч строк просто нет: ни в таблице, ни в поиске, ни в выгрузке.

    Страницами это не решается. `readRows` и так читает ВСЕ строки листа,
    а `limit` режет только ответ, — значит шесть запросов по две тысячи
    дали бы шесть полных чтений и шесть полных пересчётов вместо одного.
  */
  limit: z.coerce.number().int().min(1).max(20000).default(500),
  offset: z.coerce.number().int().min(0).default(0),
});

export const sheetRowWriteSchema = z.object({
  data: sheetRowDataSchema,
  sectionId: uuidSchema.nullable().optional(),
  /** Куда встала строка: между какими соседями */
  sortOrder: z.number().optional(),
});

export const sheetRowPatchSchema = z.object({
  data: sheetRowDataSchema.optional(),
  sectionId: uuidSchema.nullable().optional(),
  sortOrder: z.number().optional(),
  /** Ручная заливка: цвет или null, чтобы снять */
  fills: z.record(z.string(), hexColorSchema.nullable()).optional(),
  /** Оформление ячейки: вид или null, чтобы снять целиком */
  styles: z.record(z.string(), sheetCellStyleSchema.nullable()).optional(),
});

const workObjectPositionSchema = z.number().int().min(1).max(20_000);
export const workObjectMoveSchema = z.object({
  relativeTo: uuidSchema,
  placement: z.enum(['before', 'after']),
});
export type WorkObjectMove = z.infer<typeof workObjectMoveSchema>;
export const workObjectRowWriteSchema = sheetRowWriteSchema.extend({
  position: workObjectPositionSchema.optional(),
}).refine((value) => value.position === undefined || value.sortOrder === undefined, {
  message: 'Укажите только один способ изменения порядка', path: ['position'],
});
export const workObjectRowPatchSchema = sheetRowPatchSchema.extend({
  position: workObjectPositionSchema.optional(),
  move: workObjectMoveSchema.optional(),
}).refine((value) => [value.position, value.move, value.sortOrder].filter((item) => item !== undefined).length <= 1, {
  message: 'Укажите только один способ изменения порядка', path: ['position'],
});
export type WorkObjectRowWrite = z.infer<typeof workObjectRowWriteSchema>;
export type WorkObjectRowPatch = z.infer<typeof workObjectRowPatchSchema>;

export const sheetBulkPatchSchema = z.object({
  ids: z.array(z.string()).min(1).max(500),
  data: sheetRowDataSchema,
});

/**
 * Правка многих ячеек одним запросом.
 *
 * Так приходит вставка блока из книги, протягивание вниз, очистка
 * диапазона и покраска выделения. До этого каждая из них слала запрос
 * на строку в цикле: прямоугольник 20×5 — двадцать запросов, двадцать
 * записей в журнале и двадцать шагов отмены на одно действие человека.
 */
export const sheetCellsPatchSchema = z.object({
  /** Корреляция WS-эха с этой вкладкой; не заменяет проверку доступа или идемпотентность. */
  clientOpId: uuidSchema.optional(),
  cells: z
    .array(
      z.object({
        rowId: z.string(),
        sectionId: uuidSchema.nullable().optional(),
        sortOrder: z.number().optional(),
        data: sheetRowDataSchema.optional(),
        fills: z.record(z.string(), hexColorSchema.nullable()).optional(),
        styles: z.record(z.string(), sheetCellStyleSchema.nullable()).optional(),
      }),
    )
    .min(1)
    .max(2000),
});

/**
 * Запись о выносе данных с листа.
 *
 * Пишется состав, а не значения: какие колонки, сколько строк и ячеек.
 * На листах лежат номера банковских карт, и «кто и что унёс» — тот же
 * вопрос, что и для паспортных полей кандидата.
 */
export const sheetCopyLogSchema = z.object({
  columns: z.array(z.string().max(64)).max(80),
  rows: z.number().int().min(0),
  cells: z.number().int().min(0),
  rowIds: z.array(z.string()).max(100),
});

// ── Создание и правка листа ───────────────────────────────

const codeSchema = z
  .string()
  .trim()
  .min(2)
  .max(48)
  .regex(/^[a-z][a-z0-9_]*$/, 'Ключ листа — латиница в нижнем регистре, цифры и подчёркивание');

export const sheetCreateSchema = z.object({
  code: codeSchema,
  name: z.string().trim().min(1, 'Укажите название').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  isShared: z.boolean().default(false),
  visibleToRoles: z.array(roleSchema).default(['admin']),
  editableByRoles: z.array(roleSchema).default(['admin']),
});

export const sheetUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  color: hexColorSchema.nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  isShared: z.boolean().optional(),
  visibleToRoles: z.array(roleSchema).optional(),
  editableByRoles: z.array(roleSchema).optional(),
  rowLabelKey: z.string().max(64).nullable().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});

export const sheetColumnCreateSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'Ключ колонки — латиница, цифры и подчёркивание'),
  label: z.string().trim().min(1, 'Укажите заголовок').max(120),
  hint: z.string().trim().max(400).nullable().optional(),
  type: z.enum(SHEET_COLUMN_TYPES),
  options: z.array(sheetSelectOptionSchema).max(80).optional(),
  refSheetCode: z.string().max(48).nullable().optional(),
  refFilterKey: z.string().max(64).nullable().optional(),
  rule: sheetComputedRuleSchema.nullable().optional(),
  fillRules: z.array(sheetFillRuleSchema).max(20).optional(),
  width: z.number().int().min(40).max(900).optional(),
  minWidth: z.number().int().min(24).max(900).nullable().optional(),
  maxWidth: z.number().int().min(40).max(1200).nullable().optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  pinned: z.enum(['left']).nullable().optional(),
  groupLabel: z.string().trim().max(80).nullable().optional(),
  editable: z.boolean().optional(),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  visibleByDefault: z.boolean().optional(),
  required: z.boolean().optional(),
  mergeRepeats: z.boolean().optional(),
  isPii: z.boolean().optional(),
  hiddenForRoles: z.array(roleSchema).optional(),
  readonlyForRoles: z.array(roleSchema).optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  format: z.enum(SHEET_FORMATS).optional(),
  wrap: z.boolean().optional(),
});

/** Правка колонки: ключ и тип не меняются — в строках уже лежат значения. */
export const sheetColumnUpdateSchema = sheetColumnCreateSchema
  .omit({ key: true, type: true })
  .partial();

/** Порядок ярлыков внизу — перетаскиванием, как вкладки книги. */
export const sheetReorderSchema = z.object({
  codes: z.array(z.string().max(48)).min(1).max(120),
});

export const sheetColumnReorderSchema = z.object({
  /** Идентификаторы колонок в новом порядке */
  ids: z.array(uuidSchema).min(1).max(120),
});

export const sheetSectionWriteSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().max(4000).nullable().optional(),
  color: hexColorSchema.nullable().optional(),
  sortOrder: z.number().optional(),
});

export const sheetSectionCreateSchema = sheetSectionWriteSchema.extend({
  /** Повтор создания после отмены сохраняет ссылки операций на эту группу. */
  id: uuidSchema.optional(),
});

export const sheetViewWriteSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название').max(80),
  config: sheetViewConfigSchema,
  isShared: z.boolean().default(false),
  isDefault: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(10_000).default(100),
});

export const sheetRefQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  /** Значение колонки-каскада: сузить список */
  filterValue: z.string().max(160).optional(),
  includeId: z.string().max(64).optional(),
  /** Точная подпись для вставки из буфера; строка "false" не означает true. */
  exact: z.enum(['true', 'false']).transform((value) => value === 'true').optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

/** Дата для вычисляемых колонок приходит от сервера, а не из браузера. */
export const sheetTodaySchema = isoDateSchema;
